import { useState } from 'react'
import { Box, Button, Flex, Heading, IconButton, Image, Text } from '@chakra-ui/react'
import { LuExternalLink, LuImagePlus, LuPencil } from 'react-icons/lu'
import type { AgendaDetails } from '@/services/agendas'
import { AGENDA_PERMISSIONS, hasPermission } from '@/utils/agendaPermissions'
import BackButton from '@/components/BackButton'
import DeactivateAgendaButton from './DeactivateAgendaButton'
import InviteMemberButton from './InviteMemberButton'
import CreateEventButton from './CreateEventButton'
import ChangeAgendaCoverButton from './ChangeAgendaCoverButton'
import EditAgendaButton from './EditAgendaButton'

type Props = {
  agenda: AgendaDetails
}

const DESCRIPTION_LIMIT = 100;

const AgendaDetailsHeader = ({ agenda }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const hasLongDescription = (agenda.description?.length ?? 0) > DESCRIPTION_LIMIT
  const displayDescription =
    hasLongDescription && !isExpanded
      ? `${agenda.description!.slice(0, DESCRIPTION_LIMIT)}...`
      : agenda.description

  return (
    <Flex justify="space-between" gap={4} flexDir="column" flexWrap="wrap" mb="4">
      {agenda.image_path ? (
        <Box position="relative" borderRadius="xl" overflow="hidden" h={{ base: '36', sm: '48' }}>
          <Image
            src={agenda.image_path}
            alt={agenda.name}
            w="full"
            h="full"
            objectFit="cover"
            position="absolute"
            inset="0"
          />
          <Box position="absolute" inset="0" bg="blackAlpha.600" pointerEvents="none" />
          <Flex
            position="absolute"
            inset="0"
            align="center"
            justify="center"
            flexDir="column"
            gap={1}
            px={4}
            pointerEvents="none"
          >
            <Heading size="2xl" color="white" textAlign="center" textShadow="0 1px 4px rgba(0,0,0,0.5)">
              {agenda.name}
            </Heading>
          </Flex>
          <Box position="absolute" top={2} left={2} zIndex={1}>
            <BackButton color="white" disableHover />
          </Box>
          <Box position="absolute" top={2} right={2} zIndex={1}>
            <ChangeAgendaCoverButton
              renderTrigger={(onClick) => (
                <IconButton
                  size="sm"
                  aria-label="Alterar capa"
                  onClick={onClick}
                  bg="whiteAlpha.300"
                  color="white"
                  borderRadius="md"
                  _hover={{ bg: 'whiteAlpha.400' }}
                >
                  <LuPencil />
                </IconButton>
              )}
            />
          </Box>
        </Box>
      ) : (
        <Flex direction="column" gap={1} flex="1" minW="0">
          <Flex align="center" gap={2}>
            <BackButton />
            <Heading size="lg">{agenda.name}</Heading>
          </Flex>
        </Flex>
      )}

      {agenda.description && (
        <Text color="fg.muted" fontSize="sm">
          {displayDescription}
          {hasLongDescription && (
            <Text
              as="span"
              color="primary.500"
              cursor="pointer"
              ml={1}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'ver menos' : 'ver mais'}
            </Text>
          )}
        </Text>
      )}

      <Flex gap={2} flexWrap="wrap" flexDirection="row">
        {hasPermission(agenda.permissions, AGENDA_PERMISSIONS.INVITE_MEMBERS) && <InviteMemberButton />}
        {hasPermission(agenda.permissions, AGENDA_PERMISSIONS.EDIT_EVENTS) && <CreateEventButton />}
        {!agenda.image_path && (
          <ChangeAgendaCoverButton
            renderTrigger={(onClick) => (
              <Button
                size={{ base: 'xs', sm: 'sm' }}
                bgColor="blue.800"
                onClick={onClick}
              >
                <LuImagePlus />
                Alterar capa
              </Button>
            )}
          />
        )}
        <Button
          size={{ base: 'xs', sm: 'sm' }}
          onClick={() => {
            const params = new URLSearchParams({ agendaId: agenda.id })
            window.open(`/painel/calendario?${params.toString()}`, '_blank')
          }}
        >
          <LuExternalLink />
          Ver calendário
        </Button>
        <EditAgendaButton agenda={agenda} />
        <DeactivateAgendaButton />
      </Flex>
    </Flex>
  )
}

export default AgendaDetailsHeader
